import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell as BellIcon, CalendarClock, XCircle, Info, CheckCheck } from "lucide-react";
import { useNotifications, type Notif } from "../../hooks/useNotifications";
import type { FirestoreTimestamp } from "../../types/common";

function timeAgo(ts?: FirestoreTimestamp): string {
  if (!ts) return "";
  try {
    const d = typeof ts === "object" && "toDate" in ts ? ts.toDate() : new Date(ts as unknown as string);
    const diff = Date.now() - d.getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return "recién";
    if (min < 60) return `hace ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `hace ${h} h`;
    const days = Math.floor(h / 24);
    if (days < 7) return `hace ${days} d`;
    return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" });
  } catch {
    return "";
  }
}

function typeStyle(type: Notif["type"]) {
  switch (type) {
    case "reminder":
      return { Icon: CalendarClock, color: "text-sky-600", bg: "bg-sky-100" };
    case "cancel":
      return { Icon: XCircle, color: "text-rose-600", bg: "bg-rose-100" };
    default:
      return { Icon: Info, color: "text-emerald-600", bg: "bg-emerald-100" };
  }
}

export default function Bell() {
  const { items, unread, markAsRead, markAllAsRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const last8 = items.slice(0, 8);

  // Cerrar al clickear fuera
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className="relative grid place-items-center rounded-full w-10 h-10 hover:bg-slate-100 active:bg-slate-200 transition"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notificaciones${unread > 0 ? ` (${unread} sin leer)` : ""}`}
        aria-expanded={open}
      >
        <BellIcon className="w-5 h-5 text-slate-700" />
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full text-[10px] font-bold bg-rose-600 text-white ring-2 ring-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Backdrop en mobile para foco */}
          <div className="fixed inset-0 z-40 bg-black/20 sm:hidden" onClick={() => setOpen(false)} />

          <div className="fixed left-3 right-3 top-16 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-96 z-50 rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-800">
                Notificaciones {unread > 0 && <span className="text-rose-600">({unread})</span>}
              </div>
              {unread > 0 && (
                <button
                  className="text-xs text-teal-600 hover:text-teal-700 font-medium inline-flex items-center gap-1"
                  onClick={markAllAsRead}
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  Marcar todo
                </button>
              )}
            </div>

            {last8.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <BellIcon className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                <div className="text-sm text-slate-500">No hay notificaciones</div>
              </div>
            ) : (
              <ul className="max-h-[60vh] sm:max-h-96 overflow-auto divide-y divide-slate-50">
                {last8.map((n) => {
                  const { Icon, color, bg } = typeStyle(n.type);
                  const isUnread = !n.readAt;
                  return (
                    <li key={n.id} className={`px-4 py-3 flex gap-3 transition ${isUnread ? "bg-teal-50/40" : ""}`}>
                      <div className={`shrink-0 w-9 h-9 rounded-full grid place-items-center ${bg}`}>
                        <Icon className={`w-4.5 h-4.5 ${color}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-sm font-medium text-slate-800 truncate">{n.title}</div>
                          {isUnread && <span className="shrink-0 mt-1.5 w-2 h-2 rounded-full bg-teal-500" />}
                        </div>
                        <div className="text-xs text-slate-500 line-clamp-2">{n.body}</div>
                        <div className="mt-1 flex items-center gap-3">
                          <span className="text-[11px] text-slate-400">{timeAgo(n.createdAt)}</span>
                          {n.href && (
                            <Link
                              to={n.href}
                              className="text-[11px] text-teal-600 hover:underline font-medium"
                              onClick={() => {
                                markAsRead(n.id);
                                setOpen(false);
                              }}
                            >
                              Ver
                            </Link>
                          )}
                          {isUnread && (
                            <button
                              className="text-[11px] text-slate-400 hover:text-slate-600"
                              onClick={() => markAsRead(n.id)}
                            >
                              Marcar leído
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50/50 text-center">
              <Link
                className="text-xs text-teal-600 hover:text-teal-700 font-medium"
                to="/app/notifications"
                onClick={() => setOpen(false)}
              >
                Ver todas
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
